// Components
import { useMemo } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  CardItem,
  DropdownMenuCard,
} from 'components/Card';
import { parsedCustomerSignatureSelector } from 'slices/auth.slice';
import {
  selectObjectTypesList,
  selectObjectTypesMap,
} from 'slices/objectTypes.slice';
// Slices
import { reservationTemplatesSelector } from 'slices/reservationTemplate.slice';
import { useAppSelector } from 'slices/store';
import type { TType } from '@timeedit/types';
import { TEDrawerSection } from '@timeedit/ui-components';
// Types
import type { IProps } from './SectionProps.interface';
// Styles
import './Properties.scss';

const Properties = ({ relationType, onChange }: IProps) => {
  const objectTypes = useAppSelector(selectObjectTypesList);
  const objectTypeMap = useAppSelector(selectObjectTypesMap);
  const reservationTemplates = useAppSelector(reservationTemplatesSelector);
  const customerSignature = useAppSelector(parsedCustomerSignatureSelector);

  const { reservationTemplates: templates = [] } = relationType;

  const relationTypes = useMemo(
    () =>
      relationType.relationTypes.filter((rt) =>
        Boolean(objectTypeMap[rt.type])
      ),
    [objectTypeMap, relationType]
  );

  const onAddObjectType = (typeId: string) => {
    onChange('relationTypes', [
      ...relationTypes,
      { type: Number(typeId), helpType: true },
    ]);
  };

  const onDeleteObjectType = (index: number) => {
    onChange(
      'relationTypes',
      relationTypes.filter((_, i) => i !== index)
    );
  };

  const reservationTemplatesOptions = useMemo(
    () =>
      reservationTemplates
        .map((type) => ({
          key: String(type.id),
          label: type.name,
          url: `/${customerSignature}/configuration/reservation-templates?item1=${type.id}`,
        }))

        .sort((a, b) => a.label.localeCompare(b.label)),
    [reservationTemplates, customerSignature]
  );

  return (
    <TEDrawerSection label="Properties">
      <div className="relation-type-properties">
        <div data-testid="object-types">
          <Card
            title="Object types"
            tooltip="Object types"
            footer={
              <CardFooter.DropdownMenu
                menu={{
                  items: objectTypes.map((type) => ({
                    key: String(type.id),
                    label: type.name,
                  })),
                  onClick: (option) => onAddObjectType(option.key),
                }}
              />
            }
          >
            <CardBody.VirtualList data={relationTypes}>
              {(item, index) => {
                const objectType: TType = objectTypeMap[item.type];
                const relationType = relationTypes[index];

                return (
                  <CardItem.SwitchItem
                    label={objectType.name}
                    url={`/${customerSignature}/configuration/object-types?item1=${item.type}`}
                    switches={[
                      {
                        key: 'helpType',
                        label: 'Help type',
                        checked: item.helpType,
                        onChange: (checked) =>
                          onChange('relationTypes', [
                            ...relationTypes.slice(0, index),
                            { ...relationType, helpType: checked },
                            ...relationTypes.slice(index + 1),
                          ]),
                      },
                    ]}
                    onDelete={() => onDeleteObjectType(index)}
                  />
                );
              }}
            </CardBody.VirtualList>
          </Card>
        </div>
        <div data-testid="reservation-templates">
          <DropdownMenuCard
            title="Reservation templates"
            options={reservationTemplatesOptions}
            selectedKeys={templates.map((templateId) => String(templateId))}
            onChange={(values: string[]) =>
              onChange('reservationTemplates', values.map(Number))
            }
          />
        </div>
      </div>
    </TEDrawerSection>
  );
};

export default Properties;
